import classNames from 'classnames'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { toServerFormat } from 'core-components/helpers/date'
import { useWindowSize } from 'core-components/hooks/useWindowSize'
import { FlexItem } from 'core-components/sitebuilder/layout/FlexItem'
import { useCurrentSitePage } from 'core-components/sitebuilder/layout/hooks/useCurrentSitePage'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { IFlexAction } from 'core-components/sitebuilder/models/flexGrid/IFlexAction'
import { IFlexItem } from 'core-components/sitebuilder/models/flexGrid/IFlexItem'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { IFlexLayoutProps } from 'core-components/sitebuilder/models/flexGrid/IFlexLayoutProps'
import { selectEditLayoutMode } from 'core-components/sitebuilder/store/selectors/selectEditLayoutMode'
import { selectFlexActions } from 'core-components/sitebuilder/store/selectors/selectFlexActions'
import { selectFlexSettings } from 'core-components/sitebuilder/store/selectors/selectFlexSettings'
import { addFlexAction, saveFlexAction } from 'core-components/sitebuilder/store/siteBuilderSlice'

import { DragnDropItems } from './flexGrid/DragnDropItems'
import { FlexLayoutSet } from './flexGrid/FlexLayoutSet'
import { IFlexGridDnDState } from './flexGrid/models/IFlexGridDnDState'

import styles from './FlexGrid.module.scss'

export const FlexGrid = (props: IFlexLayoutProps) => {
  const items = props.visibleSettings.items || []
  const { width: windowWidth } = useWindowSize()
  const flexActions = useSelector(selectFlexActions)
  const dispatch = useDispatch()
  const currentSitePage = useCurrentSitePage()
  const showLayout = useSelector(selectEditLayoutMode)
  const flexSettings = useSelector(selectFlexSettings)

  const [parentRefs] = useState<any[]>([])
  const [currentRef, setCurrentRef] = useState()
  const [hiddenWidgets, setIsHidden] = useState<string[]>([])
  const [dragNDropData, setDragNDropData] = useState<IFlexGridDnDState>()

  const layoutRef = useRef<HTMLDivElement>(null)

  const alignContentTop = useMemo(() => {
    return items.every((item) => {
      return (
        item.widgets &&
        item.widgets.every((widget) => {
          return widget.alignContentTop
        })
      )
    })
  }, [items])

  const getFlexWrapClass = () => {
    const wrapBreakpoints = [...(props.flexItem?.wrapBreakpoints || [])]
    const canWrap = props.flexItem?.canWrap === true || props.flexItem?.canWrap === undefined
    let wrapClass = styles['flex-layout-wrap']
    if (wrapBreakpoints.length && canWrap) {
      wrapBreakpoints.sort((a, b) => (a.minWidth || 0) - (b.minWidth || 0))
      const currentBreakPoint = wrapBreakpoints.reduce((common, el) => {
        if (windowWidth >= (el.minWidth || 0)) {
          return el
        }
        return common
      }, wrapBreakpoints[0])

      if (currentBreakPoint.value === 'noWrap') {
        wrapClass = styles['flex-layout-noWrap']
      }
    } else if (!canWrap) {
      wrapClass = styles['flex-layout-noWrap']
    }

    return wrapClass
  }

  const saveModifiedSettings = (itemsSize: number[]) => {
    if (props.page && props.area && dragNDropData) {
      props.onTogglePanelChange && props.onTogglePanelChange()
      if (dragNDropData.defaultSizeOne === itemsSize[0]) {
        setDragNDropData(undefined)
        return
      }
      const changes =
        FlexDirectionBreak[`p${Math.abs(dragNDropData.defaultSizeOne - itemsSize[0])}12`]
      let newAction: IFlexAction
      if (dragNDropData.defaultSizeOne < itemsSize[0]) {
        newAction = {
          cell1: dragNDropData.itemsName[0],
          cell2: dragNDropData.itemsName[1],
          changes: changes,
        }
      } else {
        newAction = {
          cell1: dragNDropData.itemsName[1],
          cell2: dragNDropData.itemsName[0],
          changes: changes,
        }
      }

      const oldActions = flexActions[`${props.page}-${props.area}`] || []

      let oldAction: IFlexAction | undefined
      oldActions.forEach((v) => {
        if (
          (v.cell1 === newAction.cell1 && v.cell2 === newAction.cell2) ||
          (v.cell2 === newAction.cell1 && v.cell1 === newAction.cell2)
        ) {
          oldAction = v
        }
      })

      let isNewAction = true
      if (oldAction) {
        if (oldAction.cell1 === newAction.cell1) {
          newAction.changes =
            FlexDirectionBreak[`p${parseInt(oldAction.changes) + parseInt(newAction.changes)}12`]
        } else {
          if (parseInt(oldAction.changes) > parseInt(newAction.changes)) {
            const cell2 = newAction.cell2
            newAction.cell2 = newAction.cell1
            newAction.cell1 = cell2
            newAction.changes =
              FlexDirectionBreak[`p${parseInt(oldAction.changes) - parseInt(newAction.changes)}12`]
          } else if (parseInt(oldAction.changes) < parseInt(newAction.changes)) {
            newAction.changes =
              FlexDirectionBreak[`p${parseInt(newAction.changes) - parseInt(oldAction.changes)}12`]
          } else {
            isNewAction = false
            const actionsToSave = oldActions.filter(
              (v) =>
                !(
                  (v.cell1 === newAction.cell1 && v.cell2 === newAction.cell2) ||
                  (v.cell2 === newAction.cell1 && v.cell1 === newAction.cell2)
                )
            )
            dispatch(
              saveFlexAction({
                page: props.page,
                area: props.area,
                flexActions: actionsToSave,
              })
            )
          }
        }
      }
      if (isNewAction) {
        dispatch(
          addFlexAction({
            page: props.page,
            area: props.area,
            flexAction: newAction,
          })
        )
      }
    }
    setDragNDropData(undefined)
  }

  const closeDragnDrop = () => {
    setDragNDropData(undefined)
  }

  const resizeFlexItems = (items: IFlexItem[], index: number, e: any) => {
    const newDragnDropItems: any[] = [parentRefs[index - 1], parentRefs[index]]
    if (
      newDragnDropItems[0] &&
      newDragnDropItems[1] &&
      newDragnDropItems[1].offsetTop === newDragnDropItems[0].offsetTop
    ) {
      setDragNDropData({
        dragnDropItems: newDragnDropItems,
        defaultX: e.pageX,
        dragnDropIndex: index,
        itemsSize: [parseInt(items[index - 1].size), parseInt(items[index].size)],
        defaultSizeOne: parseInt(items[index - 1].size),
        itemsName: [items[index - 1].name, items[index].name],
      })
    }
  }

  const getItemLayoutSet = (item: IFlexItem): IFlexLayout => {
    const found = props.allSettings.items.find((el) => el.name === item.name)
    if (!found && item.name !== 'hidden-cells') {
      console.error(`FlexGrid: Can't find item by name ${item.name}`)
    }
    if (found?.layoutSet) {
      return found.layoutSet
    }

    return {
      items: [],
    }
  }
  const Inner = (
    <div
      ref={props.rootRef || layoutRef}
      style={{
        gap: props.flexItem?.gap ? `${props.flexItem?.gap}px` : undefined,
        margin: props.flexItem?.margin ? `${props.flexItem?.margin}px` : undefined,
      }}
      className={classNames(
        props.classNameLayout,
        styles['flex-layout-root'],
        styles['flex-layout'],
        getFlexWrapClass(),
        props.alignTop || alignContentTop ? styles['flex-layout--align-top'] : ''
      )}
    >
      {items.map((item, index) => {
        return (
          <Fragment key={index}>
            {index &&
            !dragNDropData &&
            !showLayout &&
            parentRefs[index] &&
            parentRefs[index - 1] &&
            parentRefs[index - 1].offsetTop === parentRefs[index].offsetTop ? (
              <div className={styles.dropRoot} key={`dragAreaBetweenWidgetCells${index}`}>
                <div
                  className={classNames(
                    styles.dropDiv,
                    item.name && items[index - 1].name ? styles.dropDivBlue : styles.dropDivRed
                  )}
                  onMouseDown={(e) => {
                    if (item.name && items[index - 1].name) {
                      resizeFlexItems(items, index, e)
                    }
                  }}
                >
                  <div className={styles.dropDivLine} />
                </div>
              </div>
            ) : null}
            <FlexItem
              onTogglePanelChange={props.onTogglePanelChange}
              togglePanelOpen={props.togglePanelOpen}
              onResize={props.onResize}
              resize={props.resize}
              parent={currentRef}
              parentResized={props.parentResized}
              parentRefs={parentRefs}
              allowEdit={props.allowEdit}
              flexLayoutClass={FlexGrid}
              alignTop={props.alignTop}
              widgetParams={props.widgetParams}
              classes={{
                hasSettings: styles['flex-layout__container--has-setting'],
                headerSettings: styles['flex-layout__header'],
                controlElement: styles['flex-layout__control-element'],
                isDrop: styles['flex-layout__widgets--is-drop'],
                widgets: styles['flex-layout__widgets'],
                canDrop: styles['flex-layout__widgets--can-drop'],
                column: classNames({
                  [styles.col]: true,
                  'content-hidden': hiddenWidgets.includes(item.name),
                }),
                columnNoGrow: styles['col--no-grow'],
              }}
              item={item}
              itemLayoutSet={getItemLayoutSet(item)}
              key={`${index}`}
              id={`${index}`}
              onPositionChange={(item: IFlexItem, direction: 'up' | 'down') => {
                if (!props.allSettings?.items) {
                  return
                }
                const items = [...props.allSettings.items]
                const delta = direction === 'down' ? 1 : -1
                const index = items.findIndex((el) => el.name === item.name)
                const newIndex = index + delta
                if (
                  newIndex >= items.length ||
                  newIndex < 0 ||
                  items[newIndex].widgets.length === 0
                ) {
                  return
                }
                const swappedItem = items[newIndex]
                items[newIndex] = item
                items[index] = swappedItem

                props.updateFlexLayout({
                  ...props.allSettings,
                  items,
                })
              }}
              onNameChange={(item: IFlexItem, newName: string) => {
                if (props.allSettings && props.allSettings.items) {
                  if (item) {
                    const oldName = item.name
                    const index = props.allSettings.items.findIndex((el) => el.name === item.name)
                    props.allSettings.items.splice(index, 1, {
                      ...item,
                      name: newName,
                    })
                    if (item.customWidgetSettings && item.customWidgetSettings.tabs) {
                      const defaultSettings = currentSitePage.defaultSettings
                      const settings = {
                        ...defaultSettings,
                        ...flexSettings,
                      }
                      let changed = false
                      let newSettings = structuredClone(flexSettings)
                      const date = new Date()
                      Object.keys(settings).map((key) => {
                        if (key.indexOf(oldName) > -1) {
                          const newKey = key.replace(oldName, newName)
                          newSettings = {
                            ...newSettings,
                            [`${props.page ? props.page + '-' : ''}${newKey}`]: {
                              ...settings[key],
                              version: toServerFormat(date),
                            },
                          }
                          changed = true
                        }
                      })
                      if (changed) {
                        props.updateFlexLayout(
                          {
                            ...props.allSettings,
                            items: props.allSettings.items.slice(),
                          },
                          newSettings
                        )
                        return
                      }
                    }
                  }
                  props.updateFlexLayout({
                    ...props.allSettings,
                    items: props.allSettings.items.slice(),
                  })
                }
              }}
              setState={(newItem: IFlexItem | undefined, prevName?: string) => {
                if (props.allSettings && props.allSettings.items) {
                  const items = [...props.allSettings.items]
                  if (newItem) {
                    const index = items.findIndex((el) => el.name === (prevName || newItem.name))
                    items.splice(index, 1, newItem)
                  } else {
                    items.splice(index, 1)
                  }
                  props.updateFlexLayout({
                    ...props.allSettings,
                    items,
                  })
                }
              }}
              page={props.page}
              area={props.area}
              renderCustomSettingsWidget={props.renderCustomSettingsWidget}
              renderCustomWidget={props.renderCustomWidget}
            />
          </Fragment>
        )
      })}
    </div>
  )

  useEffect(() => {
    setCurrentRef((props.rootRef && props.rootRef.current) || layoutRef.current)
  }, [props.rootRef])

  return (
    <>
      {dragNDropData && dragNDropData.dragnDropItems[0] && dragNDropData.dragnDropItems[1] ? (
        <DragnDropItems
          defaultX={dragNDropData.defaultX}
          widthOne={dragNDropData.dragnDropItems[0].offsetWidth}
          widthTwo={dragNDropData.dragnDropItems[1].offsetWidth}
          leftOne={dragNDropData.dragnDropItems[0].offsetLeft}
          topOne={dragNDropData.dragnDropItems[0].offsetTop}
          heightOne={dragNDropData.dragnDropItems[0].offsetHeight}
          itemsSize={dragNDropData.itemsSize}
          saveModifiedSettings={saveModifiedSettings}
          onClose={closeDragnDrop}
        />
      ) : null}

      {showLayout ? (
        <div
          className={classNames(
            props.additionalHead ? props.classNameLayout : '',
            styles['flex-setting-layout'],
            'flex-setting-layout'
          )}
        >
          <div
            data-testid={'flexLayoutHeader'}
            className={classNames(
              styles['flex-layout-root__header'],
              styles['flex-layout__header']
            )}
          >
            <FlexLayoutSet
              width={props.width}
              className={styles['flex-layout__control-element']}
              settings={props.visibleSettings}
              updateFlexLayout={props.updateFlexLayout}
            />
            {props.additionalHead}
          </div>
          {Inner}
        </div>
      ) : (
        Inner
      )}
    </>
  )
}
